/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { Container } from "react-bootstrap";

import UsersApi from "../../api/UsersApi";
import PageFilterWrap from "../../components/PageFilterWrap";
import PageHeader from "../../components/PageHeader";
import CardLoader from "../../components/content-loader/CardLoader";
import TableLoader from "../../components/content-loader/TableLoader";
import NothingAvailable from "../../components/placeholders/NothingAvailable";
import UsersTable from "../../components/tables/UsersTable";
import { UserTaxExpertCSVColsName } from "../../config/filingConstants";
import { EToastvariant, IUserFilters, IUserResponse } from "../../config/interfaces";
import { useAppDispatch, useAppSelector } from "../../redux/Store";
import { setUserFilters, userFiltersSelector } from "../../redux/slice/FilterSlice";
import { showToastMsg } from "../../redux/slice/ToastMsgSlice";
import { UtilService } from "../../services";

let INIT_FILTER_VALUES: IUserFilters = {
  q: "",
  orderBy: "createdAt",
  order: "DESC",
  rowCount: 10
};

const Users = () => {
  const dispatch = useAppDispatch();

  const userFilters = useAppSelector(userFiltersSelector);
  const [registeredUsers, setRegisteredUsers] = useState<IUserResponse[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isPageLoaded, setIsPageLoaded] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>(
    userFilters?.q || INIT_FILTER_VALUES?.q || ""
  );
  /* TODO it would be removed if we are not going to show filters in url as url params and use userFiltersSelector */
  const [filterValues, setFilterValues] = useState<IUserFilters>(() => {
    // on load set filter values
    INIT_FILTER_VALUES = {
      ...INIT_FILTER_VALUES
    };
    // set filter values if exists (navigate back), otherwise init values
    return userFilters || INIT_FILTER_VALUES;
  });
  const [isFiltered, setIsFiltered] = useState<boolean>(false);

  // page index will start from 0 for pagination, and will be inc by 1 in api call
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);
  const [currentTimeStamp, setCurrentTimeStamp] = useState<number>(new Date().getTime());
  const [pageCount, setPageCount] = useState(0);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    getAllUsers();
  }, [currentTimeStamp]);

  const getAllUsers = async () => {
    try {
      setIsLoading(currentPageIndex === 0 && !isPageLoaded);
      setIsFetching(true);

      const adminUsersApi = UsersApi.getInstance();
      const { records, count } = await adminUsersApi.getUsers({
        search: filterValues.q,
        offset: offset,
        count: filterValues.rowCount,
        orderBy: filterValues.orderBy,
        order: filterValues.order,
        role: "user"
      });
      setRegisteredUsers(() => records);
      setPageCount(Math.ceil(Number(count / 10)));
      setIsFiltered(showClearFilter);
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    } finally {
      setIsPageLoaded(true);
      setIsLoading(false);
      setIsFetching(false);
    }
  };

  const handleOnPageChange = (event: any) => {
    if (event.selected > 0) {
      setOffset(event.selected * 10);
    } else {
      setOffset(0);
    }
    setCurrentPageIndex(Math.ceil(event.selected));
    setCurrentTimeStamp(new Date().getTime());
  };

  const handleSearchQuery = () => {
    const _values = {
      ...filterValues,
      q: searchQuery
    };
    setFilterValues(() => _values);
    dispatch(setUserFilters(_values));
    setCurrentPageIndex(0);
    setCurrentTimeStamp(new Date().getTime());
  };

  const showClearFilter = useMemo(() => {
    if (searchQuery || filterValues?.q) {
      return true;
    }
    return false;
  }, [filterValues, searchQuery]);

  const onExportButtonClick = async () => {
    try {
      setIsLoading(() => true);

      const adminUsersApi = UsersApi.getInstance();
      const { records } = await adminUsersApi.getUsers({
        count: -1,
        role: "user"
      });
      const fileName = `mytaxclub-users-${UtilService.convertToStandardDateFormat(new Date())}`;
      const flattenedData = UserTaxExpertCSVColsName(records);
      UtilService.downloadCSV(fileName, flattenedData);
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    } finally {
      setIsLoading(() => false);
    }
  };

  return (
    <>
      <Container>
        {isLoading ? (
          <>
            <PageHeader title="Users" />
            <CardLoader cardType="list" />
          </>
        ) : (
          <>
            {!registeredUsers?.length && !isFiltered ? (
              <>
                <PageHeader title="Users" />
                <div className="text-center my-5">
                  <NothingAvailable message="Currently no user is available." />
                </div>
              </>
            ) : (
              <>
                <PageHeader
                  title="Users"
                  // onExportBtnPress={() => onExportButtonClick()}
                  filtersForm={
                    <>
                      <PageFilterWrap
                        filtersForm={<></>}
                        showSearchFilter={true}
                        searchPlaceholder="Search by Name or email"
                        searchValue={searchQuery}
                        searchOnChange={(_query) => {
                          setSearchQuery(_query);
                        }}
                        onClickSearchBtn={handleSearchQuery}
                        showAdvFilters={false}
                      />
                    </>
                  }
                />

                {isFetching ? (
                  <TableLoader portalModule="user" />
                ) : (
                  <UsersTable
                    pageName="users"
                    usersData={registeredUsers}
                    pageCount={pageCount}
                    forcePage={currentPageIndex}
                    handleOnPageChange={handleOnPageChange}
                    noRecords={isFiltered && !registeredUsers.length}
                    handleRefresh={() => setCurrentTimeStamp(new Date().getTime())}
                  />
                )}
              </>
            )}
          </>
        )}
      </Container>
    </>
  );
};

export default Users;
